/**
 * Short messages.
 *
 * An error says what happened AND what to do next, in the user's language.
 * Nothing here ever shows a code number to a cashier.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type Kind = 'ok' | 'err' | 'warn' | 'info';

interface Toast {
  id: number;
  kind: Kind;
  text: string;
  action?: { label: string; run: () => void };
}

interface ToastCtx {
  show: (text: string, kind?: Kind, action?: Toast['action']) => void;
  ok: (text: string) => void;
  err: (text: string) => void;
  warn: (text: string) => void;
  /** Something just happened that a slip of the finger could have caused: say so, and offer it back. */
  undo: (text: string, label: string, run: () => void) => void;
}

const Ctx = createContext<ToastCtx | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((text: string, kind: Kind = 'info', action?: Toast['action']) => {
    const id = nextId++;
    setToasts((t) => [...t, { id, kind, text, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), action ? 6000 : kind === 'err' ? 6000 : 3200);
  }, []);

  const value = useMemo<ToastCtx>(
    () => ({
      show,
      ok: (t) => show(t, 'ok'),
      err: (t) => show(t, 'err'),
      warn: (t) => show(t, 'warn'),
      undo: (t, label, run) => show(t, 'info', { label, run }),
    }),
    [show],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.text}
            {t.action ? (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  t.action!.run();
                  setToasts((all) => all.filter((x) => x.id !== t.id));
                }}
              >
                {t.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useToast used outside the provider');
  return c;
}
